import { Editor } from '@tiptap/core';
import Image, { ImageOptions } from '@tiptap/extension-image';
import { Plugin } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { toast } from '@/shared/components/toast';

import { ATTACHMENT_MAX_FILE_SIZE } from '@/utils/constants';

import { UploadImageFn } from '../types/rich-text-editor';
import {
	ALLOWED_IMAGE_MIME_TYPES,
	IMAGE_TOO_LARGE_TOAST,
	IMAGE_UNSUPPORTED_TYPE_TOAST,
	IMAGE_UPLOAD_ERROR_TOAST,
} from './constants';
import { ImageNodeView } from './image-node';
import {
	addImagePlaceholder,
	createImageUploadPlugin,
	createUploadId,
	removeImagePlaceholder,
	updateImagePlaceholderProgress,
} from './image-upload-plugin';

export type RteImageOptions = ImageOptions & {
	uploadImage?: UploadImageFn;
};

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		rteImage: {
			insertImageFiles: (files: File[], pos?: number) => ReturnType;
		};
	}
}

/** The node always loads so saved images render; without an uploader it is display-only. */
export const canUploadImages = (editor: Editor): boolean => {
	const image = editor.extensionManager.extensions.find(({ name }) => name === RteImage.name);
	return !!(image?.options as RteImageOptions | undefined)?.uploadImage;
};

const showError = (title: string) => toast({ title, variant: 'destructive' });

const imageFilesFrom = (list?: FileList | null): File[] =>
	Array.from(list ?? []).filter((file) => file.type.startsWith('image/'));

const rejectionFor = (file: File): string | null => {
	if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) return IMAGE_UNSUPPORTED_TYPE_TOAST;
	if (file.size > ATTACHMENT_MAX_FILE_SIZE) return IMAGE_TOO_LARGE_TOAST;
	return null;
};

/** Uploads one file behind a placeholder decoration, inserting a real node only once it lands. */
async function uploadOne(editor: Editor, file: File, upload: UploadImageFn, pos?: number) {
	const uploadId = createUploadId();
	const previewUrl = URL.createObjectURL(file);

	addImagePlaceholder(editor.view, uploadId, pos ?? editor.state.selection.from, previewUrl);

	try {
		const fileUrl = await upload(file, (percent) =>
			updateImagePlaceholderProgress(editor.view, uploadId, percent)
		);

		if (editor.isDestroyed) return;

		const insertAt = removeImagePlaceholder(editor.view, uploadId);
		if (insertAt === null) return;

		editor
			.chain()
			.insertContentAt(insertAt, { type: 'image', attrs: { src: fileUrl, alt: file.name } })
			.run();
	} catch (error) {
		console.error('Rich text editor image upload failed:', error);
		if (!editor.isDestroyed) removeImagePlaceholder(editor.view, uploadId);
		showError(IMAGE_UPLOAD_ERROR_TOAST);
	} finally {
		URL.revokeObjectURL(previewUrl);
	}
}

/** Rejected files toast and are skipped; the rest upload concurrently. */
function runImageUpload(editor: Editor, files: File[], upload: UploadImageFn, pos?: number): void {
	if (editor.isDestroyed) return;

	files.forEach((file) => {
		const rejection = rejectionFor(file);

		if (rejection) {
			showError(rejection);
			return;
		}

		void uploadOne(editor, file, upload, pos);
	});
}

export const RteImage = Image.extend<RteImageOptions>({
	addOptions() {
		return {
			...this.parent?.(),
			inline: false,
			allowBase64: false,
			uploadImage: undefined,
			// No `class` on purpose: `.tiptap img` in globals.css is the single home for image styling.
			HTMLAttributes: {},
		};
	},

	addAttributes() {
		return {
			...this.parent?.(),
			width: {
				default: null,
				parseHTML: (element) => element.getAttribute('width'),
				renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
			},
			height: {
				default: null,
				parseHTML: (element) => element.getAttribute('height'),
				renderHTML: (attributes) => (attributes.height ? { height: attributes.height } : {}),
			},
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageNodeView);
	},

	addCommands() {
		return {
			insertImageFiles:
				(files: File[], pos?: number) =>
				({ editor }) => {
					const upload = this.options.uploadImage;
					if (!upload) return false;

					runImageUpload(editor, files, upload, pos);
					return true;
				},
		};
	},

	addProseMirrorPlugins() {
		return [
			createImageUploadPlugin(),
			new Plugin({
				props: {
					// Returning false hands the event back, so a display-only editor pastes as usual.
					handlePaste: (_view: EditorView, event: ClipboardEvent) => {
						const upload = this.options.uploadImage;
						if (!upload) return false;

						const files = imageFilesFrom(event.clipboardData?.files);
						if (!files.length) return false;

						event.preventDefault();
						runImageUpload(this.editor, files, upload);
						return true;
					},
					handleDrop: (view: EditorView, event: DragEvent) => {
						const upload = this.options.uploadImage;
						if (!upload) return false;

						const files = imageFilesFrom(event.dataTransfer?.files);
						if (!files.length) return false;

						event.preventDefault();
						const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
						runImageUpload(this.editor, files, upload, coords?.pos);
						return true;
					},
				},
			}),
		];
	},
});
